// ============================================================================
// Config Field Processors
// Frontend processors for client_fields, configuration, and extra_result_details
// Mirrors the pattern used in candidate_application/processors.js
// ============================================================================

/**
 * Creates filtered list of provider options based on configuration rules and form values.
 * Returns array of applicable provider values filtered by branch/business_unit/role_type/assessment_type rules.
 *
 * Second argument is `fieldContext.sources` from FieldConfigRenderer (configuration, company, form fields, etc.)
 *
 * Logic:
 * 1. Get all configured providers
 * 2. If no rules configured, return all providers
 * 3. If add_provider_rules is true, filter based on selected branch/business_unit/role_type/assessment_type
 * 4. Return filtered array (or all if no selection made yet)
 */
export const getProviderOptionsList = (formValues, context) => {
  const configuration = context?.configuration ?? {};

  const configProviders = configuration?.providers;
  const allProviders = Array.isArray(configProviders)
    ? configProviders
    : configProviders
      ? [configProviders]
      : [];

  if (allProviders.length === 0) {
    return [];
  }

  if (!configuration?.add_provider_rules) {
    return allProviders;
  }

  const rulesBasedOn = configuration?.provider_rules_based_on;
  if (!rulesBasedOn) {
    return allProviders;
  }

  const rulesKey = `provider_${rulesBasedOn}_rules`;
  const rules = configuration?.[rulesKey] || [];
  if (rules.length === 0) {
    return allProviders;
  }

  const selectedValue = formValues?.[rulesBasedOn];
  if (!selectedValue) {
    return allProviders;
  }

  // Find matching providers based on rules
  const matchingProviders = [];
  for (const rule of rules) {
    if (rule[rulesBasedOn] === selectedValue && rule.provider) {
      if (allProviders.includes(rule.provider)) {
        matchingProviders.push(rule.provider);
      }
    }
  }

  return matchingProviders.length > 0 ? [...new Set(matchingProviders)] : allProviders;
};

export const resolveAuCriminalCheckProvider = (formValues, context) => {
  const enabled = context?.enabled_company_checks || {};
  const hasAcic = !!enabled.acic_check;
  const hasAfp = !!enabled.afp;
  if (hasAcic && !hasAfp) {
    return 'acic_check';
  }
  if (hasAfp && !hasAcic) {
    return 'afp';
  }
  const selected = formValues?.au_criminal_check_provider;
  if (selected === 'acic_check' || selected === 'afp') {
    return selected;
  }
  return 'afp';
};

// Map of all config field processors
export const CONFIG_PROCESSORS = {
  getProviderOptionsList,
  resolveAuCriminalCheckProvider,
};

export default CONFIG_PROCESSORS;
